import { Link } from "@inertiajs/react"
import { Bookmark, Check, Eye, Heart, Send, User } from "lucide-react"

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import FreelancerAvatar from "@/components/tuw/hire/FreelancerAvatar"
import StarRating from "@/components/tuw/hire/StarRating"
import TierBadge from "@/components/tuw/hire/TierBadge"
import { VR } from "@/lib/viewingRoom/wording"

// The result item across Find Freelancers + Favorites — one rich row driven by a
// freelancer object, not forked per screen. `onQuickView` switches the secondary
// action to the slide-over (search); without it the row links straight to the
// full profile (favorites). The heart toggles the saved shortlist in both.
const NICHE_CAP = 4

// P8-H7 "Muster" — below its screen's breakpoint the row re-lays itself out as a
// separated bordered CARD: identity row (avatar · wrapping name+tier ·
// role/stars), the 2-line clamped headline, niche chips, a bordered stat band, a
// full-width action row, the heart pinned top-right and the compare check over
// the avatar. Card mode turns the root into a 2-column grid and makes the
// identity column `display:contents` so the headline/chips/trailing cluster
// become full-width grid rows instead of staying indented beside the avatar.
// `col2` (the name and role/stars lines) reserves the corner the heart floats
// over, so a long pen name or a tier badge never slides under it.
//
// The two screens collapse at DIFFERENT widths (Find Freelancers ≤1200,
// Favorites ≤1024) and this is ONE shared component. Tailwind's JIT only ever
// sees LITERAL class strings in source, so an interpolated `max-[${bp}px]:`
// would silently compile to no CSS at all. Both variant sets are therefore
// spelled out in full below and selected by the `cardBelow` prop — nothing here
// is composed at runtime. A third screen means a third literal block.
//
// The literals are the EXCLUSIVE edge, same convention PriceRail records: v4
// emits `max-[1201px]` as `@media (width < 1201px)`, i.e. exactly "at or below
// 1200", and `min-[1201px]` as `>= 1201`, i.e. "above 1200". The pair therefore
// partitions every width with no one-pixel gap — writing `max-[1200px]` here
// would quietly shave the boundary to ≤1199 and leave 1200 in neither band.
//
// `min-[…]:first:border-t-0` carries what used to be an unconditional
// `first:border-t-0` on the root: the list's first item drops its separator only
// while it is still a ROW — as a card it needs its own top border, and scoping
// the reset avoids two rules fighting over `border-top-width`.
const CARD = {
  // key = the inclusive "cards at or below" width the screen was ruled at
  1200: {
    root: "min-[1201px]:first:border-t-0 max-[1201px]:grid max-[1201px]:grid-cols-[auto_1fr] max-[1201px]:items-start max-[1201px]:gap-x-[13px] max-[1201px]:gap-y-[11px] max-[1201px]:rounded-[14px] max-[1201px]:border max-[1201px]:border-border max-[1201px]:bg-card max-[1201px]:px-[15px] max-[1201px]:py-[15px]",
    compare: "max-[1201px]:absolute max-[1201px]:top-[10px] max-[1201px]:left-[10px] max-[1201px]:z-[1] max-[1201px]:shadow-[0_0_0_2px_var(--card)]",
    avatar: "max-[1201px]:row-span-2 max-[1201px]:self-start",
    identity: "max-[1201px]:contents",
    col2: "max-[1201px]:min-w-0 max-[1201px]:pr-[40px]",
    span: "max-[1201px]:col-span-2 max-[1201px]:mt-0",
    headline: "max-[1201px]:col-span-2 max-[1201px]:mt-0 max-[1201px]:max-w-none max-[1201px]:line-clamp-2",
    savedHeadline: "min-[1201px]:hidden max-[1201px]:col-span-2 max-[1201px]:line-clamp-2",
    trailing: "max-[1201px]:col-span-2 max-[1201px]:ml-0 max-[1201px]:w-full max-[1201px]:flex-col max-[1201px]:flex-nowrap max-[1201px]:items-stretch max-[1201px]:justify-start max-[1201px]:gap-[10px]",
    stats: "max-[1201px]:w-full max-[1201px]:justify-between max-[1201px]:gap-[10px] max-[1201px]:rounded-[11px] max-[1201px]:border max-[1201px]:border-border max-[1201px]:bg-secondary max-[1201px]:px-[13px] max-[1201px]:py-[10px]",
    actions: "max-[1201px]:w-full max-[1201px]:flex-row max-[1201px]:gap-[9px]",
    action: "max-[1201px]:flex-1",
    heart: "max-[1201px]:absolute max-[1201px]:top-[10px] max-[1201px]:right-[10px]",
  },
  1024: {
    root: "min-[1025px]:first:border-t-0 max-[1025px]:grid max-[1025px]:grid-cols-[auto_1fr] max-[1025px]:items-start max-[1025px]:gap-x-[13px] max-[1025px]:gap-y-[11px] max-[1025px]:rounded-[14px] max-[1025px]:border max-[1025px]:border-border max-[1025px]:bg-card max-[1025px]:px-[15px] max-[1025px]:py-[15px]",
    compare: "max-[1025px]:absolute max-[1025px]:top-[10px] max-[1025px]:left-[10px] max-[1025px]:z-[1] max-[1025px]:shadow-[0_0_0_2px_var(--card)]",
    avatar: "max-[1025px]:row-span-2 max-[1025px]:self-start",
    identity: "max-[1025px]:contents",
    col2: "max-[1025px]:min-w-0 max-[1025px]:pr-[40px]",
    span: "max-[1025px]:col-span-2 max-[1025px]:mt-0",
    headline: "max-[1025px]:col-span-2 max-[1025px]:mt-0 max-[1025px]:max-w-none max-[1025px]:line-clamp-2",
    savedHeadline: "min-[1025px]:hidden max-[1025px]:col-span-2 max-[1025px]:line-clamp-2",
    trailing: "max-[1025px]:col-span-2 max-[1025px]:ml-0 max-[1025px]:w-full max-[1025px]:flex-col max-[1025px]:flex-nowrap max-[1025px]:items-stretch max-[1025px]:justify-start max-[1025px]:gap-[10px]",
    stats: "max-[1025px]:w-full max-[1025px]:justify-between max-[1025px]:gap-[10px] max-[1025px]:rounded-[11px] max-[1025px]:border max-[1025px]:border-border max-[1025px]:bg-secondary max-[1025px]:px-[13px] max-[1025px]:py-[10px]",
    actions: "max-[1025px]:w-full max-[1025px]:flex-row max-[1025px]:gap-[9px]",
    action: "max-[1025px]:flex-1",
    heart: "max-[1025px]:absolute max-[1025px]:top-[10px] max-[1025px]:right-[10px]",
  },
}

function Stat({ value, label, tone }) {
  return (
    <div>
      <div
        className={cn(
          "flex items-center gap-[5px] font-display text-[15px] font-bold",
          tone === "now" && "text-[13px] text-done",
          tone === "soon" && "text-[13px] text-muted-foreground"
        )}
      >
        {tone === "now" && <Check className="size-[13px]" strokeWidth={2.4} />}
        {value}
      </div>
      <div className="mt-[2px] text-[10px] font-semibold tracking-[0.03em] text-ink-3 uppercase">
        {label}
      </div>
    </div>
  )
}

export default function FreelancerRow({
  f,
  compareMode = false,
  compareChecked = false,
  onToggleCompare,
  isFavorite = false,
  onToggleFavorite,
  onInvite,
  onQuickView,
  savedLabel,
  cardBelow = 1200,
  // Viewing Room: a signed-out visitor sees the same figures, with the guest
  // invitation and save labels. Their profile links load the whole document,
  // so the profile's own title, canonical and robots tags arrive.
  guest = false,
}) {
  const ProfileLink = guest ? "a" : Link
  const niches = f.niches ?? []
  const shown = niches.slice(0, NICHE_CAP)
  const extra = niches.length - shown.length
  const avail = f.availability ?? {}
  const card = CARD[cardBelow] ?? CARD[1200]

  return (
    <div
      data-testid="freelancer-row"
      className={cn(
        "relative flex flex-wrap items-center gap-x-[18px] gap-y-[14px] border-t border-border px-[22px] py-[20px] transition-colors hover:bg-accent",
        card.root
      )}
    >
      {compareMode && (
        <button
          type="button"
          onClick={() => onToggleCompare?.(f)}
          aria-label="Select to compare"
          className={cn(
            "flex size-[18px] shrink-0 items-center justify-center rounded-[5px] border-[1.5px] transition-colors",
            compareChecked ? "border-foreground bg-foreground text-background" : "border-line-2 text-transparent",
            card.compare
          )}
        >
          <Check className="size-[11px]" strokeWidth={3} />
        </button>
      )}

      <FreelancerAvatar name={f.penName} src={f.avatarUrl} size={56} online={f.online} className={card.avatar} />

      <div className={cn("min-w-[240px] flex-1", card.identity)}>
        <div className={cn("flex flex-wrap items-center gap-x-[9px] gap-y-[5px] font-display text-[17.5px] leading-tight font-bold tracking-[-0.3px]", card.col2)}>
          {onQuickView ? (
            <button
              type="button"
              className="min-w-0 text-left [overflow-wrap:anywhere] hover:text-primary"
              onClick={() => onQuickView(f)}
            >
              {f.penName}
            </button>
          ) : (
            <ProfileLink href={`/freelancers/${f.id}`} className="min-w-0 [overflow-wrap:anywhere] hover:text-primary">
              {f.penName}
            </ProfileLink>
          )}
          <TierBadge tier={f.tier} className="shrink-0" />
        </div>

        <div className={cn("mt-[3px] flex flex-wrap items-center gap-[8px] text-[12.5px] text-muted-foreground", card.col2)}>
          {f.role}
          {(f.specialties ?? []).map((specialty) => (
            <span key={specialty} className="rounded-full bg-secondary px-[8px] py-[2px] text-[10.5px] font-bold text-foreground">
              {specialty}
            </span>
          ))}
          <span className="text-line-2">·</span>
          <StarRating rating={f.rating} count={f.reviews} />
        </div>

        {savedLabel ? (
          <>
            <div className={cn("mt-[7px] flex items-center gap-[6px] text-[11.5px] text-ink-3", card.span)}>
              <Bookmark className="size-[13px]" strokeWidth={2} />
              Saved {savedLabel}
            </div>
            {/* A saved row trades the headline for its "Saved …" line because the
                row has one line to spend. A CARD has the vertical room for both,
                and the card anatomy requires the clamped headline — so it renders
                below the breakpoint only (`min-[…]:hidden`), leaving the row
                layout above exactly as it has always been. */}
            {f.headline && (
              <div
                data-testid="row-headline"
                className={cn("mt-[7px] text-[12.5px] leading-[1.5] text-ink-3", card.savedHeadline)}
              >
                {f.headline}
              </div>
            )}
          </>
        ) : (
          f.headline && (
            <div
              data-testid="row-headline"
              className={cn("mt-[8px] max-w-[54ch] text-[12.5px] leading-[1.5] text-ink-3", card.headline)}
            >
              {f.headline}
            </div>
          )
        )}

        {shown.length > 0 && (
          <div className={cn("mt-[10px] flex flex-wrap gap-[6px]", card.span)}>
            {shown.map((n) => (
              <span
                key={n.key}
                className="rounded-[7px] bg-secondary px-[9px] py-[3px] text-[11px] font-semibold text-muted-foreground"
              >
                {n.label}
              </span>
            ))}
            {extra > 0 && (
              <span className="rounded-[7px] px-[9px] py-[3px] text-[11px] font-semibold text-ink-3">
                +{extra}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Trailing cluster (stats + actions + heart). `ml-auto` keeps it right of
          the identity on a wide row; on a narrow window the whole cluster wraps
          to its own line so the name column never collapses. In card mode it
          becomes the full-width stat band + action row, and the heart leaves the
          flow for the card's top-right corner. */}
      <div className={cn("ml-auto flex shrink-0 flex-wrap items-center justify-end gap-x-[18px] gap-y-[12px]", card.trailing)}>
      <div data-testid="row-stats" className={cn("flex shrink-0 gap-[22px]", card.stats)}>
        {avail.isNow ? (
          <Stat value="Now" label="Available" tone="now" />
        ) : (
          <Stat value={(avail.label || "—").replace(/^Available\s*/, "")} label="Available" tone="soon" />
        )}
        <Stat value={f.reliability != null ? `${f.reliability}%` : "—"} label="Reliable" />
        <Stat value={f.replyTime ? f.replyTime.replace("~", "") : "—"} label="Replies" />
      </div>

      <div data-testid="row-actions" className={cn("flex w-[138px] shrink-0 flex-col gap-[8px]", card.actions)}>
        <Button className={cn("justify-center", card.action)} disabled={guest && !onInvite} onClick={() => onInvite?.(f)}>
          <Send className="size-4" strokeWidth={2} />
          {guest ? VR.action.invite : "Invite"}
        </Button>
        {onQuickView ? (
          <Button variant="outline" className={cn("justify-center", card.action)} onClick={() => onQuickView(f)}>
            <Eye className="size-4" strokeWidth={2} />
            Quick view
          </Button>
        ) : (
          <Button asChild variant="outline" className={cn("justify-center", card.action)}>
            <ProfileLink href={`/freelancers/${f.id}`}>
              <User className="size-4" strokeWidth={2} />
              {guest ? VR.search.viewProfile : "View profile"}
            </ProfileLink>
          </Button>
        )}
      </div>

      <button
        type="button"
        onClick={() => onToggleFavorite?.(f)}
        disabled={guest && !onToggleFavorite}
        title={guest ? VR.action.save : isFavorite ? "Remove from favorites" : "Save to favorites"}
        aria-label={guest ? VR.action.save : isFavorite ? "Remove from favorites" : "Save to favorites"}
        className={cn(
          "flex size-[38px] shrink-0 items-center justify-center self-center rounded-[10px] border transition-colors",
          isFavorite
            ? "border-line-2 bg-secondary text-foreground"
            : "border-border bg-card text-ink-3 hover:bg-secondary hover:text-foreground",
          card.heart
        )}
      >
        <Heart className={cn("size-[18px]", isFavorite && "fill-current")} strokeWidth={2} />
      </button>
      </div>
    </div>
  )
}
